/**
 * Access-control lookup for the CRM.
 *
 * Access model — mirrored exactly in the Firestore rules
 * (`isCrmUser` / `isCrmAdmin` in firestore.rules), which are the real
 * enforcement. This module exists so the UI can show the rejection banner
 * instead of a wall of permission-denied errors.
 *
 *   1. Any verified @mountainsnow.ski address is in, as `sales`.
 *   2. Any other address is in only if it has a `skidaCrmUsers` document.
 *   3. Either route is revoked by `disabled: true` on that document.
 *   4. `admin` always requires an explicit document — domain membership alone
 *      never grants the right to rewrite stages and templates.
 */
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
} from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { CRM_COLLECTIONS } from '@/shared/firebase/collections'
import { rolesOf, STAFF_ROLES, type CrmRole, type CrmUser } from '@/types/crm'

/** Staff domain with blanket access. Keep in sync with `crmDomainOk()` in the rules. */


export const CRM_EMAIL_DOMAIN = 'mountainsnow.ski'

export function isCrmDomainEmail(email: string): boolean {
  return email.trim().toLowerCase().endsWith(`@${CRM_EMAIL_DOMAIN}`)
}

/**
 * Returns the CRM user record, or null when the account has no access.
 *
 * `emailVerified` comes from the Firebase user. An unverified address must not
 * inherit domain trust — otherwise anyone could claim a @mountainsnow.ski
 * address on a provider that does not verify it.
 */
export async function fetchCrmUser(
  email: string,
  emailVerified: boolean,
): Promise<CrmUser | null> {
  const key = email.trim().toLowerCase()
  if (!key) return null

  /**
   * Verified, or nothing — and this now applies to everybody.
   *
   * It only ever guarded the staff-domain route, which was defensible while
   * every allowlisted address was an employee. It stops being defensible the
   * moment the list holds `someone@gmail.com`: this project has email/password
   * sign-in, so anybody can register any address, and an unverified
   * registration of a listed address would be a way in as that person.
   *
   * `firestore.rules` refuses the same request. Checking here as well is what
   * turns a screen of failed reads into an honest "not signed in".
   */
  if (!emailVerified) return null

  const snap = await getDoc(doc(db, CRM_COLLECTIONS.users, key))

  if (snap.exists()) {
    const d = snap.data()
    if (d.disabled === true) return null
    return {
      email: key,
      displayName: d.displayName ?? email,
      title: d.title ?? '',
      roles: rolesOf(d),
      shareholderId: (d.shareholderId as string) ?? '',
      ambassadorId: (d.ambassadorId as string) ?? '',
    }
  }

  /**
   * No document, no access — the staff-domain shortcut is gone, 29 Aug 2026.
   *
   * A verified @mountainsnow.ski address used to be enough on its own. It is
   * not any more: the allowlist is what stands between an outsider and the
   * share register, and a second way in means the list does not tell the whole
   * story. `firestore.rules` enforces the same thing; this is the client
   * agreeing with it rather than the client deciding it.
   */
  return null
}

/**
 * Live list of everyone with an explicit CRM record.
 *
 * Note what this cannot show: staff on the @mountainsnow.ski domain are let in
 * by the rules without a document, so they will not appear here until somebody
 * adds one. That is the point of adding one — a record is what carries the name
 * and title the email templates sign with.
 */
export function subscribeToCrmUsers(
  onData: (users: CrmUser[]) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    query(collection(db, CRM_COLLECTIONS.users)),
    (snap) =>
      onData(
        snap.docs
          .map((d) => ({
            email: d.id,
            displayName: (d.data().displayName as string) ?? d.id,
            title: (d.data().title as string) ?? '',
            roles: rolesOf(d.data()),
            shareholderId: (d.data().shareholderId as string) ?? '',
            ambassadorId: (d.data().ambassadorId as string) ?? '',
            disabled: d.data().disabled === true,
            lastSignInAt:
              typeof d.data().lastSignInAt === 'number' ? (d.data().lastSignInAt as number) : null,
          }))
          // Most recently active first: the people who actually use the CRM are
          // the ones whose title matters, and they are who you came here for.
          .sort((a, b) => (b.lastSignInAt ?? 0) - (a.lastSignInAt ?? 0)),
      ),
    (err) => onError(err as Error),
  )
}

/**
 * Live list of the people who work here.
 *
 * `skidaCrmUsers` is not a staff list. 145 of its 147 records are shareholders
 * and ambassadors holding a login that shows them their own row and nothing
 * else, so anything that offers "pick a colleague" has to narrow the collection
 * before it renders — otherwise the four people who could own a piece of work
 * are buried in a scroll of holder names.
 *
 * The `where` is also what makes this readable at all. Reading a user document
 * is yourself-or-admin, widened by one `list` rule that no document outside the
 * staff roles can satisfy; a query without this clause could return a
 * shareholder row and Firestore refuses it outright rather than filtering. So
 * the clause must match the rule — see `skidaCrmUsers` in firestore.rules.
 */
export function subscribeToTeam(
  onData: (team: CrmUser[]) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    query(collection(db, CRM_COLLECTIONS.users), where('roles', 'array-contains-any', STAFF_ROLES)),
    (snap) =>
      onData(
        snap.docs
          .map((d) => ({
            email: d.id,
            displayName: (d.data().displayName as string) ?? d.id,
            title: (d.data().title as string) ?? '',
            roles: rolesOf(d.data()),
            disabled: d.data().disabled === true,
          }))
          .filter((u) => !u.disabled)
          // By name, unlike the admin list above: this one is read to find a
          // particular colleague, and who signed in last is no help for that.
          .sort((a, b) => a.displayName.localeCompare(b.displayName)),
      ),
    (err) => onError(err as Error),
  )
}

/**
 * Everyone who can still administer the CRM, other than `excluding`.
 *
 * "Still" is doing work: a record with `disabled: true` grants nothing, so it
 * does not count towards the project having an administrator. Neither does the
 * @mountainsnow.ski domain route — that grants `sales` and never `admin`, so an
 * explicit record is the only way to be one.
 */
async function otherAdmins(excluding: string): Promise<string[]> {
  const snap = await getDocs(query(collection(db, CRM_COLLECTIONS.users)))
  return snap.docs
    .filter((d) => d.id !== excluding.trim().toLowerCase())
    // Either shape — a row written before roles became a list carries only
    // `role`, and the last-admin guard must see it either way.
    .filter((d) => rolesOf(d.data()).includes('admin') && d.data().disabled !== true)
    .map((d) => d.id)
}

/** True when this address is the only administrator the CRM has left. */
export async function isLastAdmin(email: string): Promise<boolean> {
  const key = email.trim().toLowerCase()
  const snap = await getDoc(doc(db, CRM_COLLECTIONS.users, key))
  if (!snap.exists() || snap.data().role !== 'admin' || snap.data().disabled === true) return false
  return (await otherAdmins(key)).length === 0
}

/**
 * Create or update a CRM user. The document id is the lowercased address, so
 * saving the same address twice edits rather than duplicates.
 *
 * Refuses to strip the last administrator of their admin rights, whether by
 * demotion to `sales` or by disabling them. Both leave the project with nobody
 * who can edit pipeline stages, email templates or team access — and nobody
 * inside the app who can undo it, because granting admin requires being one.
 * The only way back would be the Firestore console.
 */
export async function saveCrmUser(user: CrmUser): Promise<void> {
  const key = user.email.trim().toLowerCase()

  const losingAdmin = !user.roles.includes('admin') || user.disabled === true
  if (losingAdmin && (await isLastAdmin(key))) {
    throw new Error(
      user.disabled === true
        ? 'You cannot disable the last administrator. Make somebody else an admin first.'
        : 'You cannot remove admin from the last administrator. Make somebody else an admin first.',
    )
  }

  await setDoc(
    doc(db, CRM_COLLECTIONS.users, key),
    {
      email: key,
      displayName: user.displayName.trim(),
      title: (user.title ?? '').trim(),
      /**
       * The list, and the old field kept in step.
       *
       * `role` is still written because a document is read by two things — this
       * client and `firestore.rules` — and a row written by a new client and
       * read by a stale rule deployment would otherwise grant nothing. It is the
       * first of the list, which for a single-role user is the same value it
       * always was.
       */
      roles: user.roles,
      // The highest privilege the list holds, not simply the first of it: a
      // legacy reader looking only at `role` must not see 'sales' on somebody
      // who is an admin.
      role: user.roles.includes('admin') ? 'admin' : (user.roles[0] ?? 'sales'),
      /**
       * The link an outside role reads by, and the empty string when it has
       * none — written every time rather than merged past.
       *
       * `merge: true` would otherwise leave a stale pointer behind: demote a
       * shareholder to sales and their `shareholderId` would survive, ready to
       * grant access again the moment somebody set the role back. An empty
       * string is what the rules compare against and never match.
       */
      shareholderId: user.roles.includes('shareholder') ? (user.shareholderId ?? '') : '',
      ambassadorId: user.roles.includes('ambassador') ? (user.ambassadorId ?? '') : '',
      disabled: user.disabled === true,
      // lastSignInAt is deliberately absent — it belongs to the sync, and a
      // save from this screen must not blank it.
    },
    { merge: true },
  )
}

/**
 * Your own name and title — the profile editor behind the avatar.
 *
 * Deliberately not `saveCrmUser`. That function writes `role` and `disabled`,
 * which a person editing their own profile must never touch; the rules refuse
 * an update that changes either, so calling it here would fail at best and be
 * a privilege bug at worst.
 *
 * The `setDoc` may create the document. Staff on the @mountainsnow.ski domain
 * are let in by the rules without one, so the first time such a person sets a
 * title, this is what brings their record into existence — as `sales`, which is
 * exactly what the domain route already grants them. `merge` keeps `lastSignInAt`
 * and anything else the sync owns.
 */
export async function saveOwnProfile(
  email: string,
  displayName: string,
  title: string,
  phone: string,
  company: string,
  photoUrl: string,
): Promise<void> {
  const key = email.trim().toLowerCase()
  const existing = await getDoc(doc(db, CRM_COLLECTIONS.users, key))
  const own = {
    displayName: displayName.trim(),
    title: title.trim(),
    phone: phone.trim(),
    company: company.trim(),
    photoUrl: photoUrl.trim(),
  }
  await setDoc(
    doc(db, CRM_COLLECTIONS.users, key),
    existing.exists()
      ? own
      : // `role` and `disabled` are set only when the document does not exist.
        // Writing them on every save would let anyone restore their own access
        // by opening the profile editor, which is the one thing this write must
        // never be able to do.
        { email: key, ...own, role: 'sales', disabled: false },
    { merge: true },
  )
}

/** Owner names for the lead assignment dropdown. */
/**
 * Remove somebody's CRM access.
 *
 * Deleting the document is what revokes it: the rules grant access by the
 * existence of a `skidaCrmUsers/{email}` record, so there is no separate
 * "disabled" state to keep, and a disabled record that still grants nothing
 * would be a second source of truth about who is in.
 *
 * Two removals are refused, both because they are one-way doors:
 *
 * **Yourself** — access is granted by this record existing, so deleting your
 * own locks you out immediately.
 *
 * **The last administrator** — including when that is somebody else. Only an
 * admin can grant admin, so a CRM with none has no route back from inside the
 * app; the fix would have to be made in the Firestore console.
 *
 * ⚠️ Both checks read before they write, and the client SDK cannot run a query
 * inside a transaction, so two admins removing each other at the same instant
 * could both pass. Closing that window properly needs a Cloud Function, or a
 * rule — and rules cannot count documents. Given the size of this team the race
 * is theoretical; the note is here so nobody mistakes it for airtight.
 */
export async function removeCrmUser(email: string, actingAs: string): Promise<void> {
  const target = email.trim().toLowerCase()
  // Guarded here as well as in the UI. A disabled button stops the misclick;
  // this stops the mistake surviving a refactor of the button.
  if (target && target === actingAs.trim().toLowerCase()) {
    throw new Error('You cannot remove your own access.')
  }
  if (await isLastAdmin(target)) {
    throw new Error(
      'You cannot remove the last administrator. Make somebody else an admin first.',
    )
  }
  await deleteDoc(doc(db, CRM_COLLECTIONS.users, target))
}

export async function fetchCrmUserNames(): Promise<string[]> {
  const snap = await getDocs(query(collection(db, CRM_COLLECTIONS.users)))
  return snap.docs
    .filter((d) => d.data().disabled !== true)
    .map((d) => (d.data().displayName as string) ?? d.id)
}

/**
 * Names an outside login can be linked to — admin only, by the rules.
 *
 * A shareholder or an ambassador login has to say *which* record it is, and an
 * admin picks it from a list. Nothing but the id and the name crosses into the
 * component: the register holds personnummer and addresses, and a picker has no
 * business carrying them just because it needs a label.
 *
 * A one-shot read rather than a subscription — the list is opened to choose from
 * once, not watched.
 */
export async function loadLinkTargets(): Promise<{
  shareholders: { id: string; name: string }[]
  ambassadors: { id: string; name: string }[]
}> {
  const [sh, am] = await Promise.all([
    getDocs(query(collection(db, 'skidaCrmShareholders'), orderBy('name'))),
    getDocs(query(collection(db, 'skidaCrmPeople'), orderBy('name'))),
  ])
  const names = (snap: { docs: { id: string; get: (f: string) => unknown }[] }) =>
    snap.docs.map((d) => ({ id: d.id, name: String(d.get('name') ?? d.id) }))
  return { shareholders: names(sh), ambassadors: names(am) }
}

/**
 * Every holder on the register, with the address a login would be — admin only.
 *
 * `loadLinkTargets` deliberately carries nothing but id and name, because a
 * picker has no business holding contact details. This one carries the email as
 * well, and needs to: the Team screen lists the register so a holder's access
 * can be switched on, and the address *is* the account — it becomes the
 * document id in `skidaCrmUsers`. A holder whose register row has no email
 * cannot be given a login at all, which is why the empty string is returned
 * rather than filtered out. It is what the row says.
 *
 * Nothing else crosses over. Personnummer, addresses and holdings stay in the
 * register where a screen about access has no use for them.
 */
export async function loadShareholderRoster(): Promise<
  { id: string; name: string; email: string }[]
> {
  const snap = await getDocs(query(collection(db, 'skidaCrmShareholders'), orderBy('name')))
  return snap.docs.map((d) => ({
    id: d.id,
    name: String(d.get('name') ?? d.id),
    email: String(d.get('email') ?? '')
      .trim()
      .toLowerCase(),
  }))
}

/**
 * Switch one shareholder's login on or off, from the register row.
 *
 * The register is the list; this is the switch beside each line. Off is the
 * default and it is the *absence* of a grant, not a state somebody has to set:
 * a holder with no `skidaCrmUsers` document reads as deactivated, so importing
 * a hundred rows from Folkeinvest hands out nothing.
 *
 * On creates the account — roles `['shareholder']` and the explicit
 * `shareholderId` link, which is the only thing that lets the rules answer
 * "which record is theirs". Off keeps the document and sets `disabled`, the
 * same revocation the list above performs, so switching back on does not have
 * to guess at a name that was already entered.
 *
 * Refuses an address that already holds another role. Such a person is somebody
 * the list above is about — a staff member who owns shares, or an ambassador
 * who does — and a shareholder toggle must not be able to revoke an admin's
 * access or quietly strip their other roles. The table renders their roles
 * instead of a switch for the same reason; this is that rule where it cannot be
 * refactored away.
 */
export async function setShareholderAccess(
  holder: { id: string; name: string; email: string },
  active: boolean,
): Promise<void> {
  const key = holder.email.trim().toLowerCase()
  if (!key) {
    throw new Error(
      'This holder has no email address on the register, and the address is the account.',
    )
  }

  const ref = doc(db, CRM_COLLECTIONS.users, key)
  const existing = await getDoc(ref)

  if (existing.exists()) {
    const held = rolesOf(existing.data())
    if (held.some((r) => r !== 'shareholder')) {
      throw new Error(
        `${key} already has a CRM account with other roles. Change it in the list above.`,
      )
    }
  }

  if (!active) {
    // Nothing to revoke when there was never a grant — off is the default, and
    // writing a disabled record to say so would put a row in the allowlist for
    // somebody who was never on it.
    if (!existing.exists()) return
    await setDoc(ref, { disabled: true }, { merge: true })
    return
  }

  await setDoc(
    ref,
    {
      email: key,
      // The register's name only when there is nothing there already: a name
      // typed on the account is somebody's correction, and the register is not
      // always spelled the way the person signs.
      ...(String(existing.data()?.displayName ?? '').trim()
        ? {}
        : { displayName: holder.name.trim() || key }),
      roles: ['shareholder'] satisfies CrmRole[],
      // `role` in step with the list, for a rule deployment that still reads it.
      role: 'shareholder',
      shareholderId: holder.id,
      // Never a stale ambassador pointer on an account created as a holder.
      ambassadorId: '',
      disabled: false,
    },
    { merge: true },
  )
}
